import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { UserCheck, Gem, Clock, ArrowRight, ArrowUpRight, Star } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useConfig } from "@/hooks/useConfig";
import { COMPANY, IMAGES, VALUES, TESTIMONIALS } from "@/lib/site";
import { euro } from "@/lib/api";

const ICONS = { UserCheck, Gem, Clock };

export default function Home() {
  const { config } = useConfig();
  const vehicles = config?.vehicles || [];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.heroBg} alt="Berline de luxe la nuit sur les Champs-Élysées" className="w-full h-full object-cover ken-burns" />
          <div className="absolute inset-0 hero-vignette" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl w-full px-5 lg:px-10 pb-20 lg:pb-28">
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
            className="text-gold text-xs md:text-sm tracking-[0.4em] uppercase mb-6"
          >
            Île-de-France · VTC d'exception
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1 }}
            className="font-serif text-4xl sm:text-5xl lg:text-7xl text-white max-w-4xl leading-[1.05]"
          >
            {COMPANY.name.split(" ").slice(0, 2).join(" ")}{" "}
            <span className="gold-text">{COMPANY.name.split(" ")[2]}</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.25 }}
            className="mt-5 text-lg md:text-2xl text-white/80 font-serif italic max-w-2xl"
          >
            {COMPANY.slogan}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.4 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <Link
              to="/reservation" data-testid="hero-book-btn"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-medium text-black transition-colors duration-300 hover:bg-goldsoft"
            >
              Réserver Maintenant
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/flotte" data-testid="hero-fleet-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-gold/60 px-8 py-4 text-sm font-medium text-gold transition-colors duration-300 hover:bg-gold/10"
            >
              Découvrir la Flotte
            </Link>
          </motion.div>
        </div>
      </section>

      {/* VALUES */}
      <section className="mx-auto max-w-7xl px-5 lg:px-10 py-24 lg:py-32">
        <div className="grid gap-px bg-white/10 md:grid-cols-3 rounded-xl overflow-hidden border border-white/10">
          {VALUES.map((v, i) => {
            const Icon = ICONS[v.icon];
            return (
              <Reveal key={v.title} delay={i * 0.12}>
                <div data-testid={`value-${i}`} className="bg-ink p-10 h-full">
                  <Icon size={30} className="text-gold" strokeWidth={1.4} />
                  <h3 className="mt-6 text-xl text-white">{v.title}</h3>
                  <p className="mt-3 text-sm text-white/55 leading-relaxed">{v.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* ABOUT / SPLIT */}
      <section className="mx-auto max-w-7xl px-5 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <Reveal>
          <div className="relative">
            <img src={IMAGES.interior} alt="Intérieur cuir d'une berline de luxe" className="rounded-xl w-full h-[460px] object-cover" />
            <div className="absolute -bottom-6 -right-6 hidden md:block bg-gold text-black px-8 py-6 rounded-xl">
              <p className="font-serif text-3xl">10+</p>
              <p className="text-xs tracking-widest uppercase">Ans d'expérience</p>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div>
            <p className="text-gold text-xs tracking-[0.4em] uppercase">Notre signature</p>
            <h2 className="mt-4 font-serif text-3xl lg:text-5xl text-white leading-tight">
              Un service privé d'exception
            </h2>
            <p className="mt-6 text-white/60 leading-relaxed">
              Golden Cab Transport, c'est l'assurance d'un service privé d'exception. Avec une flotte de véhicules récents
              et des chauffeurs expérimentés, nous vous garantissons un confort absolu pour vos déplacements professionnels,
              vos transferts aéroportuaires ou vos événements spéciaux.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-6 max-w-md">
              {["Transferts aéroports", "Mise à disposition", "Événementiel & mariages", "Longue distance"].map((t) => (
                <div key={t} className="flex items-center gap-3 text-sm text-white/70">
                  <span className="h-1.5 w-1.5 rounded-full bg-gold" /> {t}
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      {/* FLEET PREVIEW */}
      <section className="mx-auto max-w-7xl px-5 lg:px-10 py-16 lg:py-24">
        <Reveal>
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <p className="text-gold text-xs tracking-[0.4em] uppercase">Notre flotte</p>
              <h2 className="mt-4 font-serif text-3xl lg:text-5xl text-white">Une élégance sur roues</h2>
            </div>
            <Link to="/flotte" className="text-sm text-gold inline-flex items-center gap-2 hover:gap-3 transition-all">
              Voir toute la flotte <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>

        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {vehicles.map((v, i) => (
            <Reveal key={v.id} delay={i * 0.1}>
              <Link
                to={`/reservation?vehicle=${v.id}`}
                data-testid={`home-vehicle-${v.id}`}
                className="group block relative overflow-hidden rounded-xl border border-white/10 bg-panel"
              >
                <div className="overflow-hidden">
                  <img src={v.image} alt={v.name} className="h-64 w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="absolute top-4 right-4 bg-ink/70 backdrop-blur px-3 py-1 rounded-full text-xs text-gold">
                  {v.category}
                </div>
                <div className="p-6 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg text-white">{v.name}</h3>
                    <p className="text-xs text-white/45 mt-1">dès {euro(v.base_fare)}</p>
                  </div>
                  <ArrowUpRight size={22} className="text-gold transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 lg:py-28 overflow-hidden">
        <div className="mx-auto max-w-7xl px-5 lg:px-10 mb-12">
          <Reveal>
            <p className="text-gold text-xs tracking-[0.4em] uppercase">Ils nous font confiance</p>
            <h2 className="mt-4 font-serif text-3xl lg:text-5xl text-white">Avis de nos clients</h2>
          </Reveal>
        </div>
        <div className="relative">
          <div className="marquee-track gap-6">
            {[...TESTIMONIALS, ...TESTIMONIALS].map((t, i) => (
              <div key={i} className="w-[340px] shrink-0 rounded-xl border border-white/10 bg-panel p-8">
                <div className="flex gap-1 text-gold mb-4">
                  {[...Array(5)].map((_, j) => <Star key={j} size={15} fill="#D4AF37" strokeWidth={0} />)}
                </div>
                <p className="text-sm text-white/70 leading-relaxed italic">“{t.text}”</p>
                <div className="mt-6">
                  <p className="text-white text-sm">{t.name}</p>
                  <p className="text-white/40 text-xs">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-5 lg:px-10 pb-24 lg:pb-32">
        <Reveal>
          <div className="relative overflow-hidden rounded-2xl border border-gold/20">
            <img src={IMAGES.nightDrive} alt="Berline de luxe roulant de nuit" className="absolute inset-0 h-full w-full object-cover opacity-40" />
            <div className="relative z-10 px-8 lg:px-16 py-16 lg:py-24 text-center">
              <h2 className="font-serif text-3xl lg:text-5xl text-white max-w-2xl mx-auto leading-tight">
                Réservez votre course en <span className="gold-text">2 minutes</span>
              </h2>
              <p className="mt-4 text-white/70 max-w-xl mx-auto">{COMPANY.tagline}</p>
              <Link
                to="/reservation" data-testid="cta-book-btn"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-gold px-10 py-4 text-sm font-medium text-black transition-colors duration-300 hover:bg-goldsoft"
              >
                Réserver & Payer en ligne <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
